import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  isSignal,
  model,
  output,
  Signal,
} from "@angular/core";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIcon } from "@angular/material/icon";
import { DialogsService } from "../../services/dialogs/dialogs.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { firstValueFrom } from "rxjs";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { FormsHelperService } from "../../services/forms-helper.service";
import { SnackService } from "../../services/snack-service.service";
import { TranslateUtils } from "../../utils/translate.utils";

@Component({
  selector: "vl-big-button",
  templateUrl: "./big-button.component.html",
  styleUrls: ["./big-button.component.scss"],
  standalone: true,
  imports: [MatProgressSpinner, MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Le clic est écouté sur l'HÔTE, pas sur le div interne : un clic visant
  // l'élément <vl-big-button> (test, script, outil d'accessibilité) tombait
  // sinon dans le vide, sans erreur ni effet.
  //
  // CLAVIER (CL-17, 26/09/2026) : l'hôte est le bouton — focusable (`tabindex` 0,
  // −1 désactivé : hors de la tabulation mais il GARDE le focus quand il passe en
  // chargement, sinon le focus retombait sur <body> au milieu d'un enregistrement),
  // Entrée / Espace rejouent un CLIC sur l'hôte : exactement le même chemin
  // (confirmation, `disabledValues`, `(click)` d'un appelant) — jamais un second.
  host: {
    "(click)": "onHostClick($event)",
    "(keydown)": "onHostKeydown($event)",
    "[attr.role]": "'button'",
    "[attr.tabindex]": "_disabled() ? -1 : 0",
    "[attr.aria-disabled]": "_disabled() ? 'true' : null",
    "[attr.aria-busy]": "loading() ? 'true' : null",
    "[attr.aria-label]": "accessibleName()",
    "[class.big-button-host--full]": "fullWidth() === true",
    "[class.big-button-host--full-xs]": "fullWidth() === 'xs'",
  },
})
export class BigButtonComponent {
  text = input<string>();
  icon = input<string>();
  iconRight = input<string>();
  loading = input(false);
  disabled = input(false);
  disabledError = input<string>();
  disabledValues = input<
    (FormGroup | FormArray | boolean | Signal<boolean> | FormControl)[]
  >([]);
  disabledShowError = model<boolean>(false);
  /** `stacked` (2026-08-05) : texte SOUS l'icône, compact — pour les
   *  rangées d'actions en colonne étroite (ex. titre Zone de préparation). */
  type = input<"large" | "fit" | "rounded" | "stacked">("large");
  color = input<
    "primary" | "basic" | "warn" | "accent" | "secondary" | "canvas" | "error"
  >("accent");
  confirmationType = input<"remove" | "custom" | "none">("none");
  confirmationTitle = input<string>();
  confirmationMessage = input<string>();
  /** Nom lu par les lecteurs d'écran — OBLIGATOIRE pour un bouton sans `text` (icône seule). */
  ariaLabel = input<string>();
  /**
   * `touch` (28/09/2026) : ≥ 48 px de haut — l'action principale d'un écran d'atelier, visée au doigt
   * (ganté). Défaut `false` : 40 px (inchangé ; l'hôte, avec son padding, fait déjà 44 px).
   * ⚠️ Pas `size` : une trentaine de gabarits posent un attribut mort `size="small"`, qu'un input de
   * ce nom aurait soudain lu.
   */
  touch = input<boolean>(false);
  /**
   * Pleine largeur (28/09/2026) : `true` toujours, `"xs"` au téléphone seulement (< 600 px, le `sm`
   * du DS) — actions empilées sous le pouce, en ligne dès `sm`. Défaut `false` (inchangé).
   */
  fullWidth = input<boolean | "xs">(false);
  /**
   * Icône seule SOUS ce breakpoint (28/09/2026) : le texte se masque au téléphone (`"sm"` : < 600 px)
   * et reste le NOM du bouton (`aria-label`, sauf `ariaLabel` fourni). Remplace le
   * `[text]="isMobile() ? '' : …"` des écrans, qui décidaient d'un breakpoint en TS. Exige `icon`.
   */
  iconOnlyBelow = input<"sm" | "md" | null>(null);

  /** `ariaLabel`, sinon — texte masqué au téléphone — le texte ; sinon rien (le texte visible nomme). */
  accessibleName = computed(() => this.ariaLabel() || (this.iconOnlyBelow() && this.icon() ? this.text() : null) || null);

  action = output();
  /** Clic (ou Entrée, Espace) sur le bouton ÉTEINT, hors chargement : le formulaire hôte peut marquer
   *  le champ que le motif nomme (#19, finitions du 05/10 : le motif seul sortait loin du champ). */
  refus = output<void>();

  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  dialogService = inject(DialogsService);
  snackService = inject(SnackService);

  _disabled = computed(() => this.disabled() || this.loading());
  colorClasses = computed(() => {
    const touch = this.touch() ? " big-button-touch" : "";
    const compact = this.iconOnlyBelow() && this.icon() ? ` big-button-icon-only-below-${this.iconOnlyBelow()}` : "";
    return `big-button ${this._disabled() ? "big-button-disabled" : "big-button-enabled"} big-button-${this.color()} big-button-${this.type()}${touch}${compact}`;
  });

  confirmationTypes = {
    remove: {
      title: "Suppression",
      message:
        "Vous êtes sur le point de supprimer cet élément. Confirmez-vous ?",
    },
  };

  onHostClick($event: Event) {
    // Le bouton reste un point d'arrêt pour le clic, même désactivé : sans ça,
    // cliquer un bouton éteint déclencherait la carte qui le porte.
    $event.stopPropagation();
    void this.onAction($event);
  }

  /**
   * Entrée / Espace sur le bouton focalisé = un clic sur l'hôte. `preventDefault` :
   * Espace ne fait pas défiler la page ; `stopPropagation` : la touche ne déclenche
   * pas AUSSI un raccourci d'un parent (carte cliquable, Entrée écoutée sur le
   * document par un dialog) — sinon deux actions pour une touche. Touche maintenue
   * (`repeat`) : une seule action.
   */
  onHostKeydown(event: KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
    if (event.target !== this.host.nativeElement || event.isComposing) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat) return;
    this.host.nativeElement.click();
  }

  async onAction($event: Event) {
    $event.stopPropagation();
    if (this._disabled()) {
      // Un bouton éteint ne doit pas se contenter de ne rien faire : quand on
      // sait pourquoi il l'est, on le dit.
      if (!this.loading() && this.disabledError()) {
        this.snackService.warn(this.disabledError()!);
      }
      if (!this.loading()) this.refus.emit();
      return;
    }
    // Les valeurs manquantes se disent AVANT la confirmation : demander
    // « confirmez-vous ? » pour ensuite ne rien faire n'apprend rien.
    if (this.testDisableValues()) {
      return;
    }
    if (this.confirmationType() !== "none") {
      const text = this.buildConfirmationType();
      const dialogRef = this.dialogService.open(
        ConfirmationDialogComponent,
        "fit-content",
        "fit-content",
        {
          title: text.title,
          message: text.message,
        },
      );
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) {
        return;
      }
    }
    this.action.emit();
  }

  buildConfirmationType() {
    if (this.confirmationType() === "custom") {
      return {
        title: this.confirmationTitle(),
        message: this.confirmationMessage(),
      };
    }
    return this.confirmationTypes[this.confirmationType()];
  }

  testDisableValues(): boolean {
    let hasError = false;
    for (const value of this.disabledValues() ?? []) {
      if (typeof value === "boolean") {
        if (value) {
          hasError = true;
        }
      } else if (isSignal(value)) {
        if (value()) {
          hasError = true;
        }
      } else if (FormsHelperService.isAbstractControl(value)) {
        if (value.invalid) {
          if (value instanceof FormGroup) {
            FormsHelperService.markGroupDirty(value);
          } else if (value instanceof FormArray) {
            FormsHelperService.markArrayDirty(value);
          } else {
            FormsHelperService.markControlDirty(value);
          }
          value.updateValueAndValidity();
          hasError = true;
        }
      }
    }
    if (hasError) {
      const error =
        this.disabledError() ??
        TranslateUtils.translate("button.invalid.default");
      this.snackService.warn(error);
      this.disabledShowError.set(true);
    } else {
      this.disabledShowError.set(false);
    }
    return hasError;
  }
}
